import React, { useState, useMemo, useCallback } from 'react';
import './PlateHeatmap.css';

/* ================================================================
   PlateHeatmap — Interactive plate visualisation for Adroit
   ================================================================
   Shows a microplate (96/384/1536) as a heatmap with:
   - Colour-coded wells by value (raw or normalised)
   - Control wells highlighted with distinct borders
   - Hover tooltips with well detail + compound info
   - Click to select wells for bulk operations
   - QC metrics bar (Z', S/B, CV)
   ================================================================ */

// ── Types ──────────────────────────────────────────────────────

export interface WellData {
  position: string;
  raw_value: number | null;
  background_subtracted_value?: number | null;
  normalized_value: number | null;
  role: string;
  molecule_id: number | null;
  compound_name: string | null;
  sample_id?: string | null;
  replicate?: number | null;
  concentration: number | null;
  concentration_unit?: string;
  patent_collisions?: number;
  result_status?: string | null;
}

interface QCMetrics {
  z_prime: number | null;
  signal_to_background: number | null;
  cv_positive: number | null;
  cv_negative: number | null;
}

export interface PlateData {
  format: string;
  plate_name?: string;
  protocol_name?: string;
  hit_count?: number;
  wells: WellData[];
  qc?: QCMetrics;
}

type ShowMode = 'normalized' | 'raw' | 'role';
type ColorScale = 'viridis' | 'inhibition';

interface PlateHeatmapProps {
  plateData?: PlateData | null;
  qcMetrics?: QCMetrics | null;
  onWellClick?: (position: string, data: WellData | undefined) => void;
  showMode?: ShowMode;
}

interface FormatConfig {
  rows: number;
  cols: number;
  wellSize: number;
  gap: number;
  fontSize: number;
}

// ── Constants ──────────────────────────────────────────────────

const FORMATS: Record<string, FormatConfig> = {
  '96':   { rows: 8,  cols: 12, wellSize: 44, gap: 3, fontSize: 11 },
  '384':  { rows: 16, cols: 24, wellSize: 24, gap: 2, fontSize: 8 },
  '1536': { rows: 32, cols: 48, wellSize: 12, gap: 1, fontSize: 0 },
};

const ROLE_COLORS: Record<string, string> = {
  sample: 'transparent',
  test_compound: 'var(--primary-500)',
  positive_control: 'var(--error-500)',
  negative_control: 'var(--info-500)',
  vehicle_control: 'var(--info-500)',
  reference: 'var(--warning-500)',
  blank: 'var(--gray-500)',
  empty: 'var(--gray-700)',
};

const ROLE_STROKE: Record<string, string> = {
  test_compound: 'var(--primary-800)',
  positive_control: 'var(--error-500)',
  negative_control: 'var(--info-500)',
  vehicle_control: 'var(--info-500)',
  reference: 'var(--warning-500)',
  blank: 'var(--gray-500)',
  empty: 'var(--gray-600)',
  sample: 'var(--primary-800)',
};

// ── Helpers ────────────────────────────────────────────────────

function getRowLabel(idx: number): string {
  if (idx < 26) return String.fromCharCode(65 + idx);
  return String.fromCharCode(64 + Math.floor(idx / 26)) + String.fromCharCode(65 + (idx % 26));
}

function valueToColor(value: number | null, min: number, max: number, colorScale: ColorScale = 'viridis'): string {
  if (value == null || isNaN(value)) return 'var(--gray-800)';
  const t = max === min ? 0.5 : (value - min) / (max - min);

  if (colorScale === 'viridis') {
    const r = Math.round(68 + t * (253 - 68));
    const g = Math.round(1 + t * (231 - 1));
    const b = Math.round(84 + (t < 0.5 ? t * 2 * (158 - 84) : (158 + (t - 0.5) * 2 * (37 - 158))));
    return `rgb(${r},${g},${b})`;
  }

  if (colorScale === 'inhibition') {
    if (t < 0.5) {
      const s = t * 2;
      return `rgb(${Math.round(220 - s * 30)}, ${Math.round(50 + s * 180)}, ${Math.round(50 + s * 30)})`;
    }
    const s = (t - 0.5) * 2;
    return `rgb(${Math.round(190 - s * 140)}, ${Math.round(230 - s * 30)}, ${Math.round(80 + s * 20)})`;
  }

  return `rgb(${Math.round(20 + (1 - t) * 60)}, ${Math.round(80 + t * 160)}, ${Math.round(180 + t * 60)})`;
}

function qcColor(zprime: number | null | undefined): string {
  if (zprime == null) return 'var(--gray-500)';
  if (zprime >= 0.5) return 'var(--success-500)';
  if (zprime >= 0.2) return 'var(--warning-500)';
  return 'var(--error-500)';
}

function qcColorRaw(zprime: number | null | undefined): string {
  if (zprime == null) return 'var(--text-secondary)';
  if (zprime >= 0.5) return '#22C55E';
  if (zprime >= 0.2) return '#F59E0B';
  return '#EF4444';
}

// ── Component ──────────────────────────────────────────────────

const PlateHeatmap: React.FC<PlateHeatmapProps> = ({
  plateData = null,
  qcMetrics = null,
  onWellClick = null,
  showMode: initialShowMode = 'normalized',
}) => {
  const [showMode, setShowMode] = useState<ShowMode>(initialShowMode);
  const [hoveredWell, setHoveredWell] = useState<string | null>(null);
  const [selectedWells, setSelectedWells] = useState<Set<string>>(new Set());
  const [focusedWell, setFocusedWell] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const data = useMemo<PlateData>(() => {
    if (plateData) return plateData;
    return generateDemoPlateData('384');
  }, [plateData]);

  const format = data.format || '384';
  const config = FORMATS[format] || FORMATS['384'];
  const { rows, cols, wellSize, gap, fontSize } = config;

  const wellMap = useMemo(() => {
    const map: Record<string, WellData> = {};
    (data.wells || []).forEach(w => { map[w.position] = w; });
    return map;
  }, [data.wells]);

  const valueRange = useMemo(() => {
    const values = (data.wells || [])
      .map(w => showMode === 'raw' ? w.raw_value : w.normalized_value)
      .filter((v): v is number => v != null && !isNaN(v));
    return {
      min: values.length ? Math.min(...values) : 0,
      max: values.length ? Math.max(...values) : 100,
    };
  }, [data.wells, showMode]);

  const qc = qcMetrics || data.qc || {} as QCMetrics;

  const plateWidth = cols * (wellSize + gap) + gap + 40;
  const plateHeight = rows * (wellSize + gap) + gap + 28;

  const handleWellHover = useCallback((position: string, event: React.MouseEvent) => {
    setHoveredWell(position);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 10 });
  }, []);

  const handleWellClick = useCallback((position: string) => {
    setSelectedWells(prev => {
      const next = new Set(prev);
      if (next.has(position)) {
        next.delete(position);
        setFocusedWell(next.values().next().value || null);
      } else {
        next.add(position);
        setFocusedWell(position);
      }
      return next;
    });
    if (onWellClick) onWellClick(position, wellMap[position]);
  }, [onWellClick, wellMap]);

  return (
    <div className="plate-heatmap">
      {/* ── Header Bar ─────────────────────────────────────── */}
      <div className="plate-heatmap__header">
        <span className="plate-heatmap__header-label">
          {data.plate_name || 'Assay Plate'}
        </span>
        <span className="plate-heatmap__header-sep" />
        <span className="plate-heatmap__header-meta">{format}-well</span>
        <span className="plate-heatmap__header-sep" />
        <span className="plate-heatmap__header-protocol">
          {data.protocol_name || 'Assay Protocol'}
        </span>
        <span className="plate-heatmap__header-sep" />

        {/* QC inline */}
        <span className="plate-heatmap__qc-inline">
          <span
            className="plate-heatmap__qc-dot"
            style={{ background: qcColorRaw(qc.z_prime) }}
          />
          <span className="plate-heatmap__qc-label">Z':</span>
          <span style={{ color: qcColorRaw(qc.z_prime) }}>
            {qc.z_prime != null ? qc.z_prime.toFixed(2) : '\u2014'}
          </span>
        </span>
        <span className="plate-heatmap__header-sep" />
        <span className="plate-heatmap__header-meta">
          <span className="plate-heatmap__qc-label">S/B:</span>{' '}
          {qc.signal_to_background?.toFixed(1) || '\u2014'}
        </span>
        <span className="plate-heatmap__header-sep" />
        <span className="plate-heatmap__header-meta">
          <span className="plate-heatmap__qc-label">Hits:</span>{' '}
          <span className="plate-heatmap__hit-count">{data.hit_count ?? '\u2014'}</span>
        </span>
      </div>

      {/* ── Controls ───────────────────────────────────────── */}
      <div className="plate-heatmap__controls">
        <span className="plate-heatmap__controls-label">Display:</span>
        {(['normalized', 'raw', 'role'] as ShowMode[]).map(mode => (
          <button
            key={mode}
            onClick={() => setShowMode(mode)}
            className={`plate-heatmap__mode-btn ${showMode === mode ? 'plate-heatmap__mode-btn--active' : ''}`}
          >
            {mode === 'normalized' ? 'Percent inhibition' : mode === 'raw' ? 'Raw RFU' : 'Well roles'}
          </button>
        ))}

        {selectedWells.size > 0 && <div className="plate-heatmap__selection-controls" aria-live="polite">
          <span>{selectedWells.size} well{selectedWells.size === 1 ? '' : 's'} selected</span>
          <button onClick={() => { setSelectedWells(new Set()); setFocusedWell(null); }} className="plate-heatmap__clear-btn">Clear selection</button>
        </div>}

        <div className="plate-heatmap__legend">
          {Object.entries(ROLE_COLORS)
            .filter(([k]) => k !== 'sample' && k !== 'empty')
            .map(([role, color]) => (
              <span key={role} className="plate-heatmap__legend-item">
                <span className="plate-heatmap__legend-swatch" style={{ background: color }} />
                <span className="plate-heatmap__legend-label">{role.replace(/_/g, ' ')}</span>
              </span>
            ))}
        </div>
      </div>

      {/* ── Plate Grid ─────────────────────────────────────── */}
      <div className="plate-heatmap__grid-wrap">
        <svg
          width={plateWidth}
          height={plateHeight}
          viewBox={`0 0 ${plateWidth} ${plateHeight}`}
          className="plate-heatmap__svg"
        >
          {/* Column headers */}
          {Array.from({ length: cols }, (_, c) => (
            <text
              key={`ch-${c}`}
              x={40 + c * (wellSize + gap) + wellSize / 2}
              y={16}
              textAnchor="middle"
              className="plate-heatmap__axis-label"
              fontSize={Math.min(fontSize, 10)}
            >
              {c + 1}
            </text>
          ))}

          {/* Row labels + wells */}
          {Array.from({ length: rows }, (_, r) => (
            <g key={`row-${r}`}>
              <text
                x={28}
                y={28 + r * (wellSize + gap) + wellSize / 2 + 4}
                textAnchor="middle"
                className="plate-heatmap__axis-label"
                fontSize={Math.min(fontSize, 10)}
              >
                {getRowLabel(r)}
              </text>

              {Array.from({ length: cols }, (_, c) => {
                const pos = `${getRowLabel(r)}${String(c + 1).padStart(2, '0')}`;
                const well = wellMap[pos] || ({} as WellData);
                const value = showMode === 'raw' ? well.raw_value : well.normalized_value;
                const isSelected = selectedWells.has(pos);
                const isHovered = hoveredWell === pos;

                let fillColor: string;
                if (showMode === 'role') {
                  fillColor = ROLE_COLORS[well.role] || 'var(--gray-700)';
                  if (fillColor === 'transparent') {
                    fillColor = value != null
                      ? valueToColor(well.normalized_value, valueRange.min, valueRange.max, 'inhibition')
                      : 'var(--gray-800)';
                  }
                } else {
                  fillColor = value != null
                    ? valueToColor(value, valueRange.min, valueRange.max,
                        showMode === 'normalized' ? 'inhibition' : 'viridis')
                    : 'var(--gray-800)';
                }

                const strokeColor =
                  isSelected ? '#F59E0B'
                  : isHovered ? 'var(--primary-300)'
                  : (well.role && well.role !== 'sample') ? ROLE_STROKE[well.role] || 'var(--primary-800)'
                  : 'rgba(13,148,136,0.08)';

                const strokeWidth =
                  isSelected ? 2
                  : isHovered ? 1.5
                  : (well.role && well.role !== 'sample') ? 1.5
                  : 0.5;

                return (
                  <g key={pos}>
                    <rect
                      x={40 + c * (wellSize + gap)}
                      y={28 + r * (wellSize + gap)}
                      width={wellSize}
                      height={wellSize}
                      rx={wellSize > 20 ? 4 : 2}
                      fill={fillColor}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeDasharray={well.role === 'blank' ? '3,2' : 'none'}
                      className="plate-heatmap__well"
                      onMouseEnter={(e) => handleWellHover(pos, e)}
                      onMouseLeave={() => setHoveredWell(null)}
                      onClick={() => handleWellClick(pos)}
                    />
                    {fontSize > 0 && value != null && wellSize >= 28 && (
                      <text
                        x={40 + c * (wellSize + gap) + wellSize / 2}
                        y={28 + r * (wellSize + gap) + wellSize / 2 + 3}
                        textAnchor="middle"
                        className="plate-heatmap__well-text"
                        fontSize={Math.max(7, fontSize - 2)}
                        pointerEvents="none"
                      >
                        {showMode === 'normalized'
                          ? `${Math.round(value)}%`
                          : value >= 1000
                            ? `${(value / 1000).toFixed(0)}k`
                            : value.toFixed(0)}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          ))}
        </svg>

        {/* Colour scale legend */}
        <div className="plate-heatmap__scale">
          <span className="plate-heatmap__scale-label">
            {showMode === 'normalized' ? '0% (inactive)' : valueRange.min?.toFixed(0)}
          </span>
          <div
            className="plate-heatmap__scale-bar"
            style={{
              background: showMode === 'normalized'
                ? 'linear-gradient(90deg, rgb(220,50,50), rgb(190,230,80), rgb(50,200,100))'
                : 'linear-gradient(90deg, rgb(68,1,84), rgb(59,82,139), rgb(33,145,140), rgb(94,201,98), rgb(253,231,37))',
            }}
          />
          <span className="plate-heatmap__scale-label">
            {showMode === 'normalized' ? '100% (active)' : valueRange.max?.toFixed(0)}
          </span>
        </div>
      </div>

      {/* ── Tooltip ────────────────────────────────────────── */}
      {hoveredWell && wellMap[hoveredWell] && (() => {
        const w = wellMap[hoveredWell];
        return (
          <div
            className="plate-heatmap__tooltip"
            style={{ left: tooltipPos.x, top: tooltipPos.y }}
          >
            <div className="plate-heatmap__tooltip-header">
              {hoveredWell}
              <span className="plate-heatmap__tooltip-role">
                {w.role?.replace(/_/g, ' ')}
              </span>
            </div>
            {w.compound_name && (
              <div className="plate-heatmap__tooltip-row">
                <span className="plate-heatmap__tooltip-label">Compound: </span>
                <span className="plate-heatmap__tooltip-compound">{w.compound_name}</span>
              </div>
            )}
            {w.sample_id && w.sample_id !== w.compound_name && (
              <div className="plate-heatmap__tooltip-row">
                <span className="plate-heatmap__tooltip-label">Sample: </span>
                {w.sample_id}
              </div>
            )}
            {w.replicate != null && (
              <div className="plate-heatmap__tooltip-row">
                <span className="plate-heatmap__tooltip-label">Replicate: </span>
                {w.replicate}
              </div>
            )}
            {w.concentration != null && (
              <div className="plate-heatmap__tooltip-row">
                <span className="plate-heatmap__tooltip-label">Conc: </span>
                {w.concentration} {w.concentration_unit || '\u00B5M'}
              </div>
            )}
            {w.raw_value != null && (
              <div className="plate-heatmap__tooltip-row">
                <span className="plate-heatmap__tooltip-label">Raw: </span>
                {w.raw_value.toFixed(1)}
              </div>
            )}
            {w.background_subtracted_value != null && (
              <div className="plate-heatmap__tooltip-row">
                <span className="plate-heatmap__tooltip-label">Background: </span>
                {w.background_subtracted_value.toFixed(1)}
              </div>
            )}
            {w.normalized_value != null && (
              <div className="plate-heatmap__tooltip-row">
                <span className="plate-heatmap__tooltip-label">% Inh: </span>
                <span style={{ color: w.normalized_value >= 50 ? 'var(--success-500)' : 'var(--error-500)' }}>
                  {w.normalized_value.toFixed(1)}%
                </span>
              </div>
            )}
            {w.result_status && <div className="plate-heatmap__tooltip-row"><span className="plate-heatmap__tooltip-label">Status: </span>{w.result_status}</div>}
            {(w.patent_collisions ?? 0) > 0 && (
              <div className="plate-heatmap__tooltip-patent">
                {w.patent_collisions} patent collision{w.patent_collisions! > 1 ? 's' : ''}
              </div>
            )}
          </div>
        );
      })()}

      {focusedWell && wellMap[focusedWell] && (() => {
        const well = wellMap[focusedWell];
        const fields = [
          ['Plate / well', `${data.plate_name || 'Assay plate'} / ${focusedWell}`],
          ['Role', well.role?.replace(/_/g, ' ') || '—'],
          ['Compound', well.compound_name || '—'],
          ['Sample', well.sample_id || '—'],
          ['Concentration', well.concentration == null ? '—' : `${well.concentration} ${well.concentration_unit || 'µM'}`],
          ['Replicate', well.replicate ?? '—'],
          ['Raw RFU', well.raw_value == null ? '—' : well.raw_value.toLocaleString('en-GB')],
          ['Background RFU', well.background_subtracted_value == null ? '—' : well.background_subtracted_value.toLocaleString('en-GB')],
          ['% inhibition', well.normalized_value == null ? '—' : `${well.normalized_value.toFixed(1)}%`],
          ['Status', well.result_status || '—'],
        ];
        return <section className="plate-heatmap__selection-panel" aria-label={`Selected well ${focusedWell} details`}>
          <div className="plate-heatmap__selection-heading"><div><span>Selected well</span><strong>{focusedWell}</strong></div><span>{selectedWells.size > 1 ? `${selectedWells.size} wells in selection` : 'Click another well to add it'}</span></div>
          <div className="plate-heatmap__selection-grid">{fields.map(([label, value]) => <div key={String(label)}><span>{label}</span><strong>{String(value)}</strong></div>)}</div>
        </section>;
      })()}

      {/* ── Stats Cards ────────────────────────────────────── */}
      <div className="plate-heatmap__stats">
        {[
          { label: "Z' Factor", value: qc.z_prime?.toFixed(3), color: qcColorRaw(qc.z_prime) },
          { label: 'Signal / Background', value: qc.signal_to_background?.toFixed(1), color: '#0D9488' },
          { label: 'CV% (Pos Controls)', value: qc.cv_positive ? `${qc.cv_positive.toFixed(1)}%` : null, color: (qc.cv_positive ?? 99) < 10 ? '#22C55E' : '#EF4444' },
          { label: 'CV% (Neg Controls)', value: qc.cv_negative ? `${qc.cv_negative.toFixed(1)}%` : null, color: (qc.cv_negative ?? 99) < 10 ? '#22C55E' : '#EF4444' },
          { label: 'Hits (\u226550% Inh)', value: data.hit_count ?? null, color: '#22C55E' },
          { label: 'Wells with Data', value: (data.wells || []).filter(w => w.raw_value != null).length, color: 'var(--text-muted)' },
        ].map((stat, i) => (
          <div key={i} className="plate-heatmap__stat-card">
            <div className="plate-heatmap__stat-label">{stat.label}</div>
            <div className="plate-heatmap__stat-value" style={{ color: stat.color }}>
              {stat.value ?? '\u2014'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlateHeatmap;


// ================================================================
// DEMO DATA GENERATOR
// ================================================================

function generateDemoPlateData(format: string = '384'): PlateData {
  const config = FORMATS[format];
  const { rows, cols } = config;
  const wells: WellData[] = [];

  const negControlCols = [1, 2];
  const posControlCols = [23, 24];
  const negMean = 45000, negSD = 3000;
  const posMean = 5000, posSD = 800;

  const compoundNames = [
    'Gefitinib', 'Erlotinib', 'Lapatinib', 'Afatinib', 'Osimertinib',
    'Neratinib', 'Dacomitinib', 'Vandetanib', 'Icotinib', 'Canertinib',
  ];

  let hitCount = 0;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const pos = `${getRowLabel(r)}${String(c + 1).padStart(2, '0')}`;
      const colNum = c + 1;

      let role = 'sample';
      let rawValue: number;
      let compoundName: string | null = null;
      let concentration: number | null = null;
      let patentCollisions = 0;

      if (negControlCols.includes(colNum)) {
        role = 'negative_control';
        rawValue = negMean + (Math.random() - 0.5) * negSD * 2;
      } else if (posControlCols.includes(colNum)) {
        role = 'positive_control';
        rawValue = posMean + (Math.random() - 0.5) * posSD * 2;
      } else {
        const isHit = Math.random() < 0.08;
        if (isHit) {
          rawValue = posMean + Math.random() * (negMean - posMean) * 0.4;
          compoundName = compoundNames[Math.floor(Math.random() * compoundNames.length)];
          patentCollisions = Math.random() < 0.3 ? Math.floor(Math.random() * 5) + 1 : 0;
          hitCount++;
        } else {
          rawValue = negMean + (Math.random() - 0.5) * negSD * 3;
        }
        concentration = 10;
      }

      const normValue = 100 * (negMean - rawValue) / (negMean - posMean);

      wells.push({
        position: pos,
        raw_value: Math.round(rawValue * 10) / 10,
        normalized_value: Math.round(normValue * 10) / 10,
        role,
        molecule_id: role === 'sample' ? Math.floor(Math.random() * 100000) : null,
        compound_name: compoundName,
        concentration,
        concentration_unit: '\u00B5M',
        patent_collisions: patentCollisions,
      });
    }
  }

  const posValues = wells.filter(w => w.role === 'positive_control').map(w => w.raw_value!);
  const negValues = wells.filter(w => w.role === 'negative_control').map(w => w.raw_value!);
  const mean = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
  const std = (arr: number[]) => { const m = mean(arr); return Math.sqrt(arr.reduce((s, v) => s + (v - m) ** 2, 0) / arr.length); };
  const cv = (arr: number[]) => (std(arr) / mean(arr)) * 100;

  const zPrime = 1 - (3 * (std(posValues) + std(negValues)) / Math.abs(mean(posValues) - mean(negValues)));

  return {
    format,
    plate_name: 'EGFR-Screen-047-003',
    protocol_name: 'EGFR Kinase Inhibition (10\u00B5M SP)',
    hit_count: hitCount,
    wells,
    qc: {
      z_prime: zPrime,
      signal_to_background: mean(negValues) / mean(posValues),
      cv_positive: cv(posValues),
      cv_negative: cv(negValues),
    },
  };
}
